// Local storage based data management service
class LocalStorageService {
  constructor() {
    this.USERS_KEY = 'opomnik_users'
    this.INVOICES_KEY = 'opomnik_invoices'
    this.ARCHIVED_KEY = 'opomnik_archived'
    this.CURRENT_USER_KEY = 'opomnik_current_user'
    this.initializeStorage()
  }

  initializeStorage() {
    if (!localStorage.getItem(this.USERS_KEY)) {
      localStorage.setItem(this.USERS_KEY, JSON.stringify([]))
    }
    if (!localStorage.getItem(this.INVOICES_KEY)) {
      localStorage.setItem(this.INVOICES_KEY, JSON.stringify([]))
    }
    if (!localStorage.getItem(this.ARCHIVED_KEY)) {
      localStorage.setItem(this.ARCHIVED_KEY, JSON.stringify([]))
    }
  }

  // User management
  async createUser(userData) {
    const users = this.getUsers()
    const existingUser = users.find(u => u.email === userData.email)
    
    if (existingUser) {
      throw new Error('Uporabnik s to e-pošto že obstaja')
    }

    const newUser = {
      id: Date.now().toString(),
      email: userData.email,
      password: userData.password, // V produkciji bi to moralo biti hashirano
      name: userData.name || 'Uporabnik',
      role: 'admin',
      createdAt: new Date().toISOString()
    }

    users.push(newUser)
    localStorage.setItem(this.USERS_KEY, JSON.stringify(users))
    
    return {user: newUser, error: null}
  }

  async signIn(credentials) {
    const users = this.getUsers()
    const user = users.find(u => 
      u.email === credentials.email && 
      u.password === credentials.password
    )

    if (!user) {
      throw new Error('Napačni podatki za prijavo')
    }

    const sessionUser = {...user}
    delete sessionUser.password // Ne shranjevamo gesla v sejo
    localStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(sessionUser))
    
    return {user: sessionUser, error: null}
  }

  async signOut() {
    localStorage.removeItem(this.CURRENT_USER_KEY)
    return {error: null}
  }

  getCurrentUser() {
    const userJson = localStorage.getItem(this.CURRENT_USER_KEY)
    return userJson ? JSON.parse(userJson) : null
  }

  getUsers() {
    const usersJson = localStorage.getItem(this.USERS_KEY)
    return usersJson ? JSON.parse(usersJson) : []
  }

  // Invoice management
  async getInvoices(userId) {
    const invoices = this.getAllInvoices()
    return {data: invoices.filter(invoice => invoice.user_id === userId), error: null}
  }

  async addInvoice(invoiceData) {
    const invoices = this.getAllInvoices()
    const newInvoice = {
      id: Date.now().toString(),
      ...invoiceData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    invoices.push(newInvoice)
    localStorage.setItem(this.INVOICES_KEY, JSON.stringify(invoices))
    
    return {data: newInvoice, error: null}
  }

  async updateInvoice(invoiceId, updateData) {
    const invoices = this.getAllInvoices()
    const index = invoices.findIndex(inv => inv.id === invoiceId)
    
    if (index === -1) {
      throw new Error('Račun ni najden')
    }

    invoices[index] = {
      ...invoices[index],
      ...updateData,
      updated_at: new Date().toISOString()
    }
    
    localStorage.setItem(this.INVOICES_KEY, JSON.stringify(invoices))
    
    return {data: invoices[index], error: null}
  }

  async deleteInvoice(invoiceId) {
    const invoices = this.getAllInvoices()
    const filteredInvoices = invoices.filter(inv => inv.id !== invoiceId)
    
    localStorage.setItem(this.INVOICES_KEY, JSON.stringify(filteredInvoices))
    
    return {error: null}
  }

  getAllInvoices() {
    const invoicesJson = localStorage.getItem(this.INVOICES_KEY)
    return invoicesJson ? JSON.parse(invoicesJson) : []
  }

  // Archived invoices management
  async getArchivedInvoices(userId) {
    const archived = this.getAllArchivedInvoices()
    return {data: archived.filter(invoice => invoice.user_id === userId), error: null}
  }

  async addArchivedInvoice(invoiceData) {
    const archived = this.getAllArchivedInvoices()
    const newArchived = {
      id: Date.now().toString(),
      ...invoiceData,
      archived_at: new Date().toISOString()
    }

    archived.push(newArchived)
    localStorage.setItem(this.ARCHIVED_KEY, JSON.stringify(archived))
    
    return {data: newArchived, error: null}
  }

  async deleteArchivedInvoice(invoiceId) {
    const archived = this.getAllArchivedInvoices()
    const filteredArchived = archived.filter(inv => inv.id !== invoiceId)
    
    localStorage.setItem(this.ARCHIVED_KEY, JSON.stringify(filteredArchived))
    
    return {error: null}
  }

  getAllArchivedInvoices() {
    const archivedJson = localStorage.getItem(this.ARCHIVED_KEY)
    return archivedJson ? JSON.parse(archivedJson) : []
  }

  // Utility methods
  exportData() {
    return {
      users: this.getUsers(),
      invoices: this.getAllInvoices(),
      archived: this.getAllArchivedInvoices(),
      exportDate: new Date().toISOString()
    }
  }

  importData(data) {
    if (data.users) {
      localStorage.setItem(this.USERS_KEY, JSON.stringify(data.users))
    }
    if (data.invoices) {
      localStorage.setItem(this.INVOICES_KEY, JSON.stringify(data.invoices))
    }
    if (data.archived) {
      localStorage.setItem(this.ARCHIVED_KEY, JSON.stringify(data.archived))
    }
  }

  clearAllData() {
    localStorage.removeItem(this.USERS_KEY)
    localStorage.removeItem(this.INVOICES_KEY)
    localStorage.removeItem(this.ARCHIVED_KEY)
    localStorage.removeItem(this.CURRENT_USER_KEY)
    this.initializeStorage()
  }
}

const localStorageService = new LocalStorageService()
export default localStorageService